import { Button } from "components/Button/Button";
import { stringToFormFieldComponent } from "components/Form";
import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { DateTimeFormComponent } from "components/Form/components/DateTime/DateTimeFormComponent";
import { Form } from "components/Form/components/Form/Form";
import { FormComponentPropLanguages } from "components/Form/components/FormComponentProps";
import { LocationFormField } from "components/Form/components/LocationFormField/LocationFormField";
import { RadioGroupFormComponent } from "components/Form/components/RadioGroup/RadioGroupFormComponent";
import { seedSchemaFormValues } from "components/Form/components/SchemaForm/seedSchemaFormValues";
import { SwitchFormComponent } from "components/Form/components/Switch/SwitchFormComponent";
import { Modal } from "components/Modal/Modal";
import { rolesAutocompleteSelector } from "components/RoleSelect/RoleSelect";
import { getRecordRoles, getRecordRolesNote } from "components/RoleSelect/recordRoles";
import { Callout } from "components/Form/components/Callout/Callout";
import { RoleSelectAutocompleteFormComponent } from "components/RoleSelect/RoleSelectAutocompleteFormComponent";
import { fetchUserList } from "containers/Auth/screens/IdentityManagement/UserList.service";
import { Action } from "containers/Resources/components/CommonActions/Action";
import { getSelectedLocationKey } from "containers/Resources/components/CommonActions/MoveToLocation/MoveResourcesToLocation";
import { useMoveToLocation } from "containers/Resources/components/CommonActions/MoveToLocation/useMoveToLocation";
import {
    ExecutionPriorityField,
    setDefaultPriorityMutator,
    useGetPriority,
} from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/ExecutionPriorityField";
import {
    MaxRuntimeMinutesField,
    useMaxRuntimeMinutes,
} from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/MaxRuntimeMinutesField";
import dayjs from "dayjs";
import { User } from "graphql/generated/graphql";
import { YAMLException } from "js-yaml";
import { isEqual } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import { Field, Form as FinalForm } from "react-final-form";
import { useSelector } from "react-redux";
import { useMount } from "react-use";
import { parseYaml } from "utils/valueTransformers";

/**
 * Width of the "Run with options" dialog. The form is laid out in two columns
 * (run options left, input value + roles right) so the whole form fits without
 * inner scrolling on a typical desktop viewport; Modal.scss caps it at the
 * viewport width and collapses to one column on narrow screens
 * (ui-design-principle-modals-dialogs: explicit width from the size scale).
 */
export const RUN_WITH_OPTIONS_MODAL_WIDTH = "1040px";

/**
 * "Run with options" dialog for a flow: opens a modal form that lets the user
 * run the flow with explicit options (productive, wrappers, schedule, remote
 * workspace, location, input value, roles, priority, max runtime).
 *
 * The run request itself is performed by the injected `runAction` prop (the
 * flow resource's run action). `onSubmit` awaits it so react-final-form's
 * `submitting` flag stays true for the whole request duration — that single
 * flag drives the in-flight feedback mandated by `ui-design-principle-buttons`
 * ("Async-triggering buttons — in-progress feedback"): the Run button shows a
 * spinner in its icon slot + self-disables, the Cancel button sibling-disables,
 * and the field block goes non-interactive so the submitted values can't be
 * mutated mid-request. Extracted from `flow.tsx` so this behaviour is unit-
 * testable in isolation (the config module it used to live in pulls in the
 * whole resource graph).
 */
export const RunWithOptions = ({
    actionData,
    dispatch,
    history,
    isDisabled,
    saveForm,
    runAction,
}: {
    actionData: any;
    dispatch: any;
    history: any;
    isDisabled?: boolean;
    saveForm: () => Promise<unknown>;
    runAction: Action;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const recordRolesNote = getRecordRolesNote(getRecordRoles(actionData?.resourceData));

    const { getPriorityValue, defaultPriorityValue } = useGetPriority();
    const {
        getMaxRuntimeMinutesInitialValue,
        getMaxRuntimeMinutesSubmitValue,
        workspaceMaxRuntimeMinutesValue,
    } = useMaxRuntimeMinutes();

    const userListSelector = (state: any) => state?.dataStore?.allUsers?.user_list?.user_list ?? [];
    const userList: User[] = useSelector(userListSelector);
    const currentUserRoleList = useMemo(
        () =>
            userList
                .find((entry) => entry.id === actionData?.userData?.identity_id)
                ?.identity_role_plain_list_identity_id?.reduce(
                    (roles: { name: string; id: string; propagate: boolean }[], currentRole) => {
                        const roleWithPropagate = currentRole?.propagate
                            ? {
                                  name: currentRole?.role_id_role?.name ?? "",
                                  id: currentRole.role_id_role?.id ?? "",
                                  propagate: true,
                              }
                            : null;
                        return roleWithPropagate ? [...roles, roleWithPropagate] : roles;
                    },
                    [],
                ) ?? [],
        [actionData?.userData?.identity_id, userList],
    );
    const roles = useSelector(rolesAutocompleteSelector).map((role) => ({
        ...role,
        propagate: false,
    }));

    const defaultLocationId =
        actionData.resourceData?.project_id ?? actionData.resourceData?.bundle_id;
    const { selectedLocation, changeValue, options } = useMoveToLocation("flow");

    useMount(() => {
        fetchUserList(dispatch);
    });

    const initialValues = useMemo(
        () => ({
            is_productive: true,
            use_wrappers: true,
            run_later: "false",
            scheduled_for: null,
            run_here: "true",
            roles: currentUserRoleList,
            location: defaultLocationId,
            inputValueAndSchema: {
                // Seed the editable value model with the schema's effective defaults so
                // the code-view and submit payload match what the form view renders on
                // open (otherwise the model is `null`: running unchanged drops the shown
                // defaults, and the first edit greys out every other field). See
                // seedSchemaFormValues for the full rationale.
                values: seedSchemaFormValues(actionData.resourceData?.input_schema),
                schema: actionData.resourceData?.input_schema,
            },
            priority: getPriorityValue(actionData.resourceData?.priority),
            max_runtime_minutes: getMaxRuntimeMinutesInitialValue(
                actionData.resourceData?.max_runtime_minutes,
            ),
        }),
        [
            actionData.resourceData?.input_schema,
            actionData.resourceData?.max_runtime_minutes,
            actionData.resourceData?.priority,
            currentUserRoleList,
            defaultLocationId,
            getMaxRuntimeMinutesInitialValue,
            getPriorityValue,
        ],
    );

    const onSubmit = useCallback(
        async (formData) => {
            const unwrapped = formData.use_wrappers != null ? !formData.use_wrappers : true;
            const scheduled_for =
                (formData.run_later === "true" && dayjs(formData.scheduled_for).format()) ||
                initialValues.scheduled_for;
            const remote_workspace_name =
                (formData.run_here === "false" && formData.remote_workspace_name) || null;
            const roles = formData.roles.map(({ name, propagate }) => ({
                name,
                propagate: propagate ?? false,
            }));
            let location = {};
            if (selectedLocation) {
                location = {
                    [getSelectedLocationKey(selectedLocation.type!)]: formData.location,
                };
            }
            const additionalVariables = {
                unwrapped,
                scheduled_for,
                is_productive: formData.is_productive,
                remote_workspace_name,
                roles,
                input_value: formData.inputValueAndSchema.values,
                priority: getPriorityValue(formData.priority),
                max_runtime_minutes: getMaxRuntimeMinutesSubmitValue(formData.max_runtime_minutes),
                ...location,
            };

            // Await the run request so react-final-form's `submitting` flag stays true
            // for its whole duration — that drives the Run button's in-flight spinner and
            // the form-disable below. On success runAction navigates to the created
            // execution (this dialog unmounts); on error the resource saga surfaces the
            // failure notification, so we swallow the rejection here only to clear
            // `submitting` and keep the dialog open for a retry.
            try {
                await runAction(actionData, dispatch, history, additionalVariables);
            } catch {
                // error already surfaced by the resource saga's notification
            }
        },
        [
            actionData,
            dispatch,
            getMaxRuntimeMinutesSubmitValue,
            getPriorityValue,
            history,
            initialValues.scheduled_for,
            runAction,
            selectedLocation,
        ],
    );

    const formRef = useRef<any>(null);

    return (
        <>
            <Button
                onClick={() => saveForm().then(() => setIsOpen(true))}
                disabled={isDisabled}
                variation="clear"
                label="Run with options"
            />
            <Modal
                title="Run flow with options"
                onCancel={() => {
                    setIsOpen(false);
                    formRef.current.restart();
                }}
                isVisible={isOpen}
                className="runWithOptionsModal"
                width={RUN_WITH_OPTIONS_MODAL_WIDTH}
                closable={false}
            >
                <FinalForm
                    onSubmit={onSubmit}
                    initialValues={initialValues}
                    mutators={{
                        setDefaultPriority: (args, state, utils) =>
                            setDefaultPriorityMutator({ state, utils, defaultPriorityValue }),
                    }}
                >
                    {({ handleSubmit, values, form, pristine, submitting }) => {
                        formRef.current = form;

                        return (
                            <Form onSubmit={handleSubmit}>
                                <div
                                    className="runWithOptionsFields"
                                    data-testid="run-with-options-fields"
                                    aria-busy={submitting}
                                    // Disable the whole form while the run request is in
                                    // flight so the submitted values can't be mutated
                                    // mid-request (covers custom react-select / Monaco
                                    // inputs that a native fieldset[disabled] would miss).
                                    style={
                                        submitting
                                            ? { pointerEvents: "none", opacity: 0.6 }
                                            : undefined
                                    }
                                >
                                    <div
                                        className="runWithOptionsFields__options"
                                        data-testid="run-with-options-options-column"
                                    >
                                        <Form.Row>
                                            <Field
                                                name="is_productive"
                                                id="is_productive"
                                                fieldProps={{
                                                    label: "Productive",
                                                }}
                                                component={SwitchFormComponent}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <Field
                                                name="use_wrappers"
                                                id="use_wrappers"
                                                fieldProps={{
                                                    label: "Use wrappers",
                                                }}
                                                component={SwitchFormComponent}
                                            />
                                        </Form.Row>

                                        <Form.Row>
                                            <ExecutionPriorityField
                                                label="Execution priority"
                                                onBlur={() => {
                                                    if (!pristine) {
                                                        form.mutators.setDefaultPriority();
                                                    }
                                                }}
                                            />
                                        </Form.Row>

                                        <Form.Row>
                                            <MaxRuntimeMinutesField
                                                label="Max runtime minutes"
                                                recordType="flow"
                                                workspaceMaxRuntimeMinutesValue={
                                                    workspaceMaxRuntimeMinutesValue
                                                }
                                            />
                                        </Form.Row>

                                        <Form.Row>
                                            <Field
                                                name="run_later"
                                                id="run_later"
                                                fieldProps={{
                                                    options: [
                                                        {
                                                            label: "Run now",
                                                            value: "false",
                                                        },
                                                        {
                                                            label: "Run later",
                                                            value: "true",
                                                        },
                                                    ],
                                                }}
                                                fieldAttr={{ row: true }}
                                                component={RadioGroupFormComponent}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <Field
                                                name="scheduled_for"
                                                id="scheduled_for"
                                                component={DateTimeFormComponent}
                                                fieldAttr={{
                                                    disabled: values?.run_later === "false",
                                                }}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <Field
                                                name="run_here"
                                                id="run_here"
                                                fieldProps={{
                                                    options: [
                                                        {
                                                            label: "Run here",
                                                            value: "true",
                                                        },
                                                        {
                                                            label: "Run in remote workspace",
                                                            value: "false",
                                                        },
                                                    ],
                                                }}
                                                fieldAttr={{ row: true }}
                                                component={RadioGroupFormComponent}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <Field
                                                name="remote_workspace_name"
                                                id="remote_workspace_name"
                                                component={AutocompleteFormComponent}
                                                fieldAttr={{
                                                    disabled: values?.run_here === "true",
                                                }}
                                                fieldProps={{
                                                    label: "Sync config",
                                                    options: {
                                                        resource: "sync_config",
                                                        hasNameAsOptionValue: true,
                                                    },
                                                }}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <LocationFormField
                                                locationId={defaultLocationId}
                                                changeValue={changeValue}
                                                selectedLocation={selectedLocation}
                                                options={options}
                                                label="Run in location"
                                                isRequired={false}
                                            />
                                        </Form.Row>
                                    </div>
                                    <div
                                        className="runWithOptionsFields__input"
                                        data-testid="run-with-options-input-column"
                                    >
                                        <Form.Row>
                                            <Field
                                                name="inputValueAndSchema"
                                                component={stringToFormFieldComponent.schemaForm}
                                                fieldProps={{
                                                    lang: "yaml" as FormComponentPropLanguages,
                                                    isReadOnly: false,
                                                    columnDirection: true,
                                                    label: "Input value",
                                                }}
                                                isEqual={(valueA, valueB) => {
                                                    return isEqual(valueA, valueB);
                                                }}
                                                validation={(value) => {
                                                    try {
                                                        parseYaml(value.values);
                                                        return undefined;
                                                    } catch (error: any) {
                                                        const typedError: YAMLException = error;
                                                        const errorMessage = `${typedError?.name}: ${typedError?.reason}`;
                                                        return errorMessage;
                                                    }
                                                }}
                                            />
                                        </Form.Row>
                                        <Form.Row>
                                            <RoleSelectAutocompleteFormComponent
                                                name="roles"
                                                defaultValue={currentUserRoleList}
                                                roleOptions={roles}
                                            />
                                        </Form.Row>
                                        {recordRolesNote && (
                                            <Form.Row>
                                                <Callout
                                                    type="info"
                                                    className="runWithOptionsRecordRoles"
                                                    text={recordRolesNote}
                                                />
                                            </Form.Row>
                                        )}
                                    </div>
                                </div>
                                <Form.Row className="sticky-bottom-actions">
                                    <CancelButton
                                        onClick={() => {
                                            setIsOpen(false);
                                            form.restart();
                                        }}
                                        disabled={submitting}
                                    />
                                    <Button
                                        variation="primary"
                                        submit
                                        label="Run"
                                        loading={submitting}
                                        disabled={submitting}
                                    />
                                </Form.Row>
                            </Form>
                        );
                    }}
                </FinalForm>
            </Modal>
        </>
    );
};
